import { useEffect, useRef } from 'react';
import { useChat } from '@/context/ChatContext';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';

export function ChatWindow() {
  const {
    activeConversation,
    messages,
    isLoadingMessages,
    isSending,
    sendMessage,
    createConversation,
  } = useChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const handleSendMessage = async (content: string) => {
    let conversationId = activeConversation?.id;
    if (!conversationId) {
      // Create new conversation first
      const newConversation = await createConversation();
      conversationId = newConversation.id;
    }
    await sendMessage(content, conversationId);
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
          </div>
        ) : messages.length === 0 ? (
          <WelcomeScreen onSendMessage={handleSendMessage} disabled={isSending} />
        ) : (
          <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isSending && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area - only show when there are messages */}
      {messages.length > 0 && (
        <div className="border-t border-zinc-800 bg-base/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto p-4">
            <ChatInput onSend={handleSendMessage} disabled={isSending} />
          </div>
        </div>
      )}
    </div>
  );
}

interface WelcomeScreenProps {
  onSendMessage: (message: string) => void;
  disabled: boolean;
}

function WelcomeScreen({ onSendMessage, disabled }: WelcomeScreenProps) {
  const examples = [
    {
      icon: 'web',
      title: 'Build a Website',
      text: 'Create a landing page for my startup'
    },
    {
      icon: 'storage',
      title: 'Database Setup',
      text: 'Set up PostgreSQL for my application'
    },
    {
      icon: 'rocket_launch',
      title: 'Deploy App',
      text: 'Deploy my React app to production'
    },
    {
      icon: 'terminal',
      title: 'Manage Containers',
      text: 'Show me all my running containers'
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-4">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand to-brand-bright flex items-center justify-center">
          <span className="material-icons text-4xl text-white">auto_awesome</span>
        </div>
        <h1 className="text-3xl font-bold mb-2">
          Welcome to <span className="gradient-text">WiserWeb AI</span>
        </h1>
        <p className="text-zinc-400 max-w-lg">
          Build and deploy web applications through natural conversation.
          Tell me what you want to create and I'll help you make it happen.
        </p>
      </div>

      {/* Centered Chat Input */}
      <div className="w-full max-w-2xl mb-8 animate-fade-in">
        <ChatInput
          onSend={onSendMessage}
          disabled={disabled}
          minRows={4}
          showAnimatedPlaceholder={true}
        />
      </div>

      {/* Example Cards Below */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-4xl animate-fade-in-up">
        {examples.map((example, index) => (
          <button
            key={index}
            onClick={() => onSendMessage(example.text)}
            disabled={disabled}
            className="flex flex-col items-center gap-2 p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-center hover:bg-zinc-800 hover:border-zinc-600 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center group-hover:bg-brand/30 transition-colors">
              <span className="material-icons text-2xl text-brand group-hover:scale-110 transition-transform">
                {example.icon}
              </span>
            </div>
            <span className="text-sm font-medium text-zinc-200">{example.title}</span>
            <span className="text-xs text-zinc-500">{example.text}</span>
          </button>
        ))}
      </div>

      <p className="mt-8 text-xs text-zinc-600">
        Powered by Claude AI & WiserWebCloud
      </p>
    </div>
  );
}
